import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../App';

const C = {
  card: '#1C1C28', border: '#2A2A3A', surface: '#16161F',
  accent: '#E8A2B8', accentSoft: '#C97A9A', teal: '#7ECECA', gold: '#D4AF7A',
  text: '#F0EEF5', muted: '#8A8A9E', danger: '#FF6B7A', green: '#7ECBA5',
};

const SKIN_EMOJIS = ['😢', '😕', '😐', '🙂', '😊'];
const SKIN_COLORS = ['#FF6B7A', '#FF9F7A', '#D4AF7A', '#7ECBA5', '#7ECECA'];

const CONDITIONS = ['Acne', 'Dryness', 'Oiliness', 'Redness', 'Sensitivity', 'Texture', 'Hydrated', 'Breakout'];

const skinTips = [
  { phase: '🌑 Menstrual', color: '#E8A2B8', tip: 'Skin is most sensitive. Focus on hydration and gentle products. Avoid harsh treatments.' },
  { phase: '🌒 Follicular', color: '#D4AF7A', tip: 'Skin becomes clearer as oestrogen rises. Good time for active treatments like exfoliation.' },
  { phase: '🌕 Ovulatory', color: '#7ECECA', tip: 'Peak clarity and radiance. Oil production increases — lighter moisturisers work best.' },
  { phase: '🌖 Luteal', color: '#7ECBA5', tip: 'Breakouts more likely. Use richer moisturisers and focus on barrier repair. Reduce actives.' },
];

export default function Skin({ user }) {
  const [skinLogs, setSkinLogs] = useState([]);
  const [quality, setQuality] = useState(null);
  const [conditions, setConditions] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const token = localStorage.getItem('authToken');

  useEffect(() => {
    axios.get(`${API_URL}/api/logs?category=skinflare`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setSkinLogs(r.data.slice(-5).reverse()))
      .catch(() => {});
  }, [saved]);

  const handleSave = async () => {
    if (quality === null) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/logs`, {
        date: new Date().toISOString().split('T')[0],
        category: 'skinflare',
        value: quality,
        notes: `${conditions.join(', ')}${notes ? '. ' + notes : ''}`,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSaved(true);
      setQuality(null);
      setConditions([]);
      setNotes('');
    } catch (err) {}
    setSaving(false);
  };

  const toggleCondition = (c) => setConditions(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <h2 style={{ fontFamily: "'DM Serif Display',serif", color: C.text, fontSize: 22, margin: 0 }}>Skin Health</h2>

      {/* Log today */}
      <div style={{ background: C.card, borderRadius: 18, padding: 16, border: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace', marginBottom: 12 }}>LOG TODAY'S SKIN</div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginBottom: 12 }}>
          {SKIN_EMOJIS.map((emoji, idx) => (
            <button key={idx} onClick={() => setQuality(idx + 1)}
              style={{ flex: 1, padding: 12, fontSize: 22, background: quality === idx + 1 ? `${SKIN_COLORS[idx]}22` : C.surface, border: `1.5px solid ${quality === idx + 1 ? SKIN_COLORS[idx] : C.border}`, borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s' }}>
              {emoji}
            </button>
          ))}
        </div>
        {quality && <div style={{ textAlign: 'center', fontSize: 11, color: SKIN_COLORS[quality - 1], fontFamily: 'monospace', marginBottom: 12 }}>RATING {quality}/5</div>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 6, marginBottom: 12 }}>
          {CONDITIONS.map(c => (
            <button key={c} onClick={() => toggleCondition(c)}
              style={{ padding: '8px', background: conditions.includes(c) ? `${C.teal}22` : C.surface, border: `1.5px solid ${conditions.includes(c) ? C.teal : C.border}`, color: conditions.includes(c) ? C.teal : C.muted, borderRadius: 8, cursor: 'pointer', fontSize: 11, transition: 'all 0.15s' }}>
              {c}
            </button>
          ))}
        </div>

        <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any notes..."
          style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 12px', color: C.text, fontSize: 12, resize: 'none', outline: 'none', marginBottom: 10, height: 60 }} />

        <button onClick={handleSave} disabled={quality === null || saving}
          style={{ width: '100%', background: quality !== null ? C.accent : C.border, color: '#0D0D12', border: 'none', borderRadius: 10, padding: '12px', fontSize: 13, fontWeight: 700, cursor: quality !== null ? 'pointer' : 'default' }}>
          {saving ? 'Saving...' : 'Save skin log'}
        </button>
        {saved && <div style={{ textAlign: 'center', marginTop: 8, fontSize: 11, color: C.green, fontFamily: 'monospace' }}>✓ SAVED</div>}
      </div>

      {/* Recent entries */}
      {skinLogs.length > 0 && (
        <div style={{ background: C.card, borderRadius: 16, padding: 16, border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace', marginBottom: 10 }}>RECENT SKIN LOGS</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {skinLogs.map((log, idx) => {
              const v = Math.min(parseInt(log.value) - 1, 4);
              return (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 10, borderBottom: idx < skinLogs.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                  <span style={{ fontSize: 22 }}>{SKIN_EMOJIS[v] || '😐'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: C.text }}>{log.notes || 'No notes'}</div>
                    <div style={{ fontSize: 10, color: C.muted }}>{new Date(log.date).toLocaleDateString('en-GB')}</div>
                  </div>
                  <div style={{ fontSize: 10, color: SKIN_COLORS[v], fontFamily: 'monospace', border: `1px solid ${SKIN_COLORS[v]}`, borderRadius: 6, padding: '2px 8px' }}>{v + 1}/5</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Cycle tips */}
      <div style={{ background: C.card, borderRadius: 16, padding: 16, border: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace', marginBottom: 12 }}>SKIN BY CYCLE PHASE</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {skinTips.map((t, i) => (
            <div key={i} style={{ background: C.surface, borderRadius: 10, padding: 12 }}>
              <div style={{ fontSize: 13, color: t.color, fontWeight: 600, marginBottom: 4 }}>{t.phase}</div>
              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{t.tip}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
